import { useCallback, useState } from 'react';

import useApolloClient from 'api/hooks/useApolloClient';
import useMutation from 'api/hooks/useMutation';
import permissions from 'api/Apollo/permissions';

import { mutation, buildVariables } from './bulk-update-pipeline-integrations-mutation-builder';
import chunkInputsByByteSize from './chunkInputsByByteSize';
import prepareBulkUpdateMutationInputs from './prepareBulkUpdateMutationInputs';

export default function useBulkUpdatePipelineIntegrations() {
  const client = useApolloClient();
  const [executeMutation, { isAllowed }] = useMutation(mutation, {
    context: { permission: permissions.updatePipelineIntegration },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);

  const bulkUpdate = useCallback(
    async updateRequests => {
      const mutationInputs = prepareBulkUpdateMutationInputs(updateRequests);
      const batches = chunkInputsByByteSize(mutationInputs);
      const summary = {
        total: updateRequests.length,
        batchCount: batches.length,
        succeededCount: 0,
        failedCount: 0,
        failedPipelineIds: [],
        errors: [],
      };

      setIsSubmitting(true);
      setProgress({ completed: 0, total: summary.total });

      try {
        for (const batch of batches) {
          try {
            const result = await executeMutation({ variables: buildVariables(batch) });
            if (result?.errors?.length) {
              throw result.errors;
            }
            summary.succeededCount += batch.length;
          } catch (error) {
            summary.failedCount += batch.length;
            summary.failedPipelineIds.push(...batch.map(input => input.pipelineId));
            summary.errors.push(error);
          } finally {
            setProgress({ completed: summary.succeededCount + summary.failedCount, total: summary.total });
          }
        }

        if (summary.succeededCount > 0) {
          evictPipelinesCache(client.cache);
        }
      } finally {
        setIsSubmitting(false);
        setProgress(null);
      }

      return summary;
    },
    [client, executeMutation]
  );

  return [bulkUpdate, { isAllowed, loading: isSubmitting, progress }];
}

function evictPipelinesCache(cache) {
  cache.batch({
    update: batchCache => {
      batchCache.evict({ fieldName: 'pipelines' });
      batchCache.evict({ fieldName: 'pipelines_aggregate' });
    },
  });
  cache.gc();
}
